import type * as Models from "@/redux/models"
import type { ChartDatum, MinDateMaxDate } from "@/base/types"
import type { PayloadAction, Reducer } from "@reduxjs/toolkit"
import type { ValuesType } from "utility-types"

import * as AppStorage from "@/base/async-storage"
import {
  createFarmJoinCodeAsync,
  loadFlowMeteringAnalyticsAsync,
  loadFlowMeteringReportAsync,
} from "@/base/farmhq-api"
import { loadActiveFarmAsync } from "@/base/load-app"
import { makeValidator } from "@/base/type-guards"
import { createSlice, isAnyOf } from "@reduxjs/toolkit"

import type { RootState } from "./root.reducer"

import { signOutAsync } from "./auth.reducer"

export interface FarmAnalyticsData {
  reportBytes: string | null
}

export interface FlowAnalyticsData {
  deviceTotals: Array<{
    dailyCumsumAcreFeet: ChartDatum[]
    dailyPeakGpm: ChartDatum[]
    dailyVolumeTotalAcreFeet: ChartDatum[]
    deviceName: string | null
    devicePeakGpm: number
    deviceVolumeTotalAcreFeet: number
  }>
  farmTotals: {
    dailyCumsumAcreFeet: ChartDatum[]
    dailyVolumeTotalAcreFeet: ChartDatum[]
    farmTotalAcreFeet: number
  }
}
export const FlowAnalyticsSources = {
  ESTIMATED: "estimated",
  METERED: "metered",
} as const
export const isValidFlowAnalyticsSource = makeValidator(
  Object.values(FlowAnalyticsSources),
)
export type FlowAnalyticsSource = ValuesType<typeof FlowAnalyticsSources>

interface FarmState {
  farmAnalytics: FarmAnalyticsData &
    MinDateMaxDate & {
      flowAnalytics: FlowAnalyticsData | null
      source: FlowAnalyticsSource
    }
  // users: ModelState<OtherFarmUser, string>
  joinCode?:
    | Models.FarmJoinCode
    | { codeStatus: "NOT_CREATED"; codeStr?: undefined }
    | null
  newCodeCreated?: boolean
}

const initialState: FarmState = {
  // users: Models.getInitialEntityState(),
  farmAnalytics: {
    dateMsMax: AppStorage.STORAGE_DEFAULTS.farmAnalyticsDateMsMax,
    dateMsMin: AppStorage.STORAGE_DEFAULTS.farmAnalyticsDateMsMin,
    flowAnalytics: null,
    reportBytes: null,
    source: FlowAnalyticsSources.METERED,
  },
}

const slice = createSlice({
  extraReducers: (builder) =>
    builder
      .addCase(
        AppStorage.readLocalStorageAsync.fulfilled,
        (state, { payload }) => {
          // MIN DATE
          state.farmAnalytics.dateMsMin = payload.farmAnalyticsDateMsMin
          // MAX DATE
          state.farmAnalytics.dateMsMax = payload.farmAnalyticsDateMsMax
        },
      )
      .addCase(loadActiveFarmAsync.pending, (state) => {
        // usersAdapter.removeAll(state.users)
        state.joinCode = undefined
        state.farmAnalytics.reportBytes = null
      })
      .addCase(loadActiveFarmAsync.fulfilled, (state, { payload }) => {
        state.joinCode = payload.farmJoinCode
        if (payload.farmJoinCode === null) {
          state.joinCode = { codeStatus: "NOT_CREATED" }
        } else {
          state.joinCode = payload.farmJoinCode
        }
      })
      .addCase(createFarmJoinCodeAsync.fulfilled, (state, { payload }) => {
        state.newCodeCreated = true
        state.joinCode = payload
      })
      // MIN DATE
      .addCase(
        AppStorage.setFarmAnalyticsDateMsMin.pending,
        (state, { meta }) => {
          state.farmAnalytics.dateMsMin = meta.arg
        },
      )
      // MAX DATE
      .addCase(
        AppStorage.setFarmAnalyticsDateMsMax.pending,
        (state, { meta }) => {
          state.farmAnalytics.dateMsMax = meta.arg
        },
      )
      .addCase(loadFlowMeteringReportAsync.fulfilled, (state) => {
        state.farmAnalytics.reportBytes = null
      })

      .addCase(
        loadFlowMeteringAnalyticsAsync.fulfilled,
        (state, { payload }) => {
          state.farmAnalytics.flowAnalytics = payload
        },
      )
      .addMatcher(isAnyOf(signOutAsync.fulfilled), () => {
        return { ...initialState }
      })
      .addDefaultCase((state) => {
        state.newCodeCreated = false
      }),
  initialState,
  name: "farm",
  reducers: {
    setFlowAnalyticsSource: (
      state,
      action: PayloadAction<FarmState["farmAnalytics"]["source"]>,
    ) => {
      state.farmAnalytics.source = action.payload
    },
  },
})

const farm: Reducer<FarmState> = slice.reducer
export default farm
export const { setFlowAnalyticsSource } = slice.actions
export function getFlowAnalyticsSourceFromState(state: RootState) {
  return state.farm.farmAnalytics.source
}

export const getFarmJoinCodeFromState = (state: RootState) => {
  return state.farm.joinCode
}
export function getFarmJoinCodeStatusFromState(
  state: RootState,
): Models.FarmJoinCode["codeStatus"] {
  return getFarmJoinCodeFromState(state)?.codeStatus ?? "NOT_CREATED"
}

export function getFarmJoinCodeStringFromState(state: RootState) {
  return getFarmJoinCodeFromState(state)?.codeStr
}
